import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { Link } from 'react-router-dom'
import useSWR from 'swr'
import Select from 'react-select'
import { fetchLiveControlMatches } from '../services/api'
import { Badge, Button, FormattedDateTime, Pagination, ToggleGroup, ToggleOption, CompetitorSelect } from '../components'
import { useLiveControlFilters, useCopyToClipboard } from '../hooks'
import { formatMatchStatus } from '../utils/format'
import { PUBLISHING_STATUS_OPTIONS, CATEGORY_OPTIONS } from '../constants'
import type { LiveControlStatus } from '../hooks'
import pageStyles from '../styles/page.module.scss'
import listTableStyles from '../styles/list-table.module.scss'
import buttonStyles from '../components/Button.module.scss'
import selectStyles from '../styles/react-select.module.scss'
import styles from './LiveControl.module.scss'

interface LiveControlMatch {
    tc_match_id: string
    ext_event_id: string
    title: string
    home_name: string
    away_name: string
    round: string | null
    tc_venue_id: string | null
    venue_name: string | null
    venue_city: string | null
    tc_competition_id: string | null
    competition_name: string | null
    competition_category: string | null
    season_name: string | null
    start_time: string | null
    end_time: string | null
    resume_time: string | null
    status: string | null
    match_status: string | null
    live_control_status: LiveControlStatus | 'unpublished'
    publishing: {
        tc_pub_content_id: string | null
        short_id: string | null
        status: string | null
        segment_number: number | null
        scheduled_publish_at: string | null
        published_at: string | null
        auto_republish: boolean
        has_amagi_error: boolean
        has_publish_error: boolean
        feed_name: string | null
        is_override: boolean
    } | null
}

interface LiveControlResponse {
    matches: LiveControlMatch[]
    pagination: {
        page: number
        limit: number
        total_count: number
        total_pages: number
        has_next: boolean
        has_prev: boolean
    }
    applied_filters?: {
        players?: any[]
    }
    status_counts: Record<string, number>
    time_window: {
        now: string
        upcoming_cutoff: string
        recent_cutoff: string
    }
    timestamp: string
}

const STATUS_TABS: ToggleOption<LiveControlStatus>[] = [
    { value: 'all', label: 'All', description: 'All matches from 3 hours ago to 48 hours from now' },
    { value: 'live', label: 'Live', color: 'live', description: 'Events that have started and have not ended (using start time and end time)' },
    { value: 'upcoming-soon', label: 'Upcoming (<30min)', color: 'upcoming-soon', description: 'Events with a start time in the next 30 minutes' },
    { value: 'upcoming', label: 'Upcoming (<48hr)', color: 'upcoming', description: 'Events with a start time in the next 48 hours' },
    { value: 'ended', label: 'Recently Ended (<3hr)', color: 'ended', description: 'Events with an end time in the past 3 hours' },
    { value: 'suspended', label: 'Suspended', color: 'suspended', description: 'Events with a Suspended status' },
    { value: 'problem', label: 'Problems', color: 'problem', description: 'Events with Cancelled/Abandoned status, Amagi errors, or publish errors' },
]

const REFRESH_INTERVAL_MS = 60 * 1000 // 1 minute

// Match status constants for filtering
const PROBLEM_STATUSES = ['abandoned', 'cancelled']

interface FilterOption {
    value: string
    label: string
}

const LiveControl: React.FC = () => {
    // URL-backed filter state (shareable, survives back-navigation)
    const { filters, updateFilterAndResetPage, handlePageChange } = useLiveControlFilters()
    const { status, playerIds, publishingStatuses, competitionIds, courtIds, categories, search, page } = filters

    // Clipboard copy for publishing IDs
    const { copiedId: copiedMatchId, handleCopy: handleCopyPublishingId } = useCopyToClipboard()

    // Local state for the full player objects (CompetitorSelect needs full objects for display)
    const [selectedPlayers, setSelectedPlayers] = useState<any[]>([])

    // UI-only state (not shareable)
    const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
    const limit = 50

    // Build API params
    const params = useMemo(() => {
        const p: Record<string, any> = {
            hours_upcoming: 48,
            hours_recent: 3,
            sort_order: 'asc',
        }
        if (search) {
            p.search = search
        }
        if (playerIds.length > 0) {
            p.player_ids = playerIds.join(',')
        }
        if (categories.length > 0) {
            p.categories = categories.join(',')
        }
        return p
    }, [search, playerIds, categories])

    // Fetch data with auto-refresh every minute
    const { data, isLoading, error, mutate } = useSWR<LiveControlResponse>(
        ['live-control', params],
        () => fetchLiveControlMatches(params),
        {
            revalidateOnFocus: false,
            refreshInterval: REFRESH_INTERVAL_MS,
            onSuccess: () => setLastUpdated(new Date()),
        }
    )

    const allMatches = useMemo(() => data?.matches || [], [data?.matches])
    const appliedFilters = data?.applied_filters

    // Update selected players from API response (applied_filters)
    useEffect(() => {
        if (appliedFilters?.players) {
            setSelectedPlayers(appliedFilters.players)
        } else if (playerIds.length === 0) {
            setSelectedPlayers([])
        }
    }, [appliedFilters, playerIds.length])

    // Derive unique competition options from all matches (for filter dropdown)
    const competitionOptions = useMemo(() => {
        const unique = new Map<string, FilterOption>()
        for (const m of allMatches) {
            if (m.tc_competition_id && m.competition_name) {
                unique.set(m.tc_competition_id, {
                    value: m.tc_competition_id,
                    label: m.competition_name
                })
            }
        }
        return Array.from(unique.values()).sort((a, b) => a.label.localeCompare(b.label))
    }, [allMatches])

    // Derive unique court/venue options from all matches (for filter dropdown)
    const courtOptions = useMemo(() => {
        const unique = new Map<string, FilterOption>()
        for (const m of allMatches) {
            if (m.tc_venue_id && m.venue_name) {
                const label = m.venue_city ? `${m.venue_name}, ${m.venue_city}` : m.venue_name
                unique.set(m.tc_venue_id, {
                    value: m.tc_venue_id,
                    label
                })
            }
        }
        return Array.from(unique.values()).sort((a, b) => a.label.localeCompare(b.label))
    }, [allMatches])

    // Apply all dropdown filters (publishing status, competition, court)
    const dropdownFilteredMatches = useMemo(() => {
        let result = allMatches

        // Publishing status filter
        if (publishingStatuses.length > 0) {
            result = result.filter(m => {
                const pubStatus = m.publishing?.status || 'UNPUBLISHED'
                return publishingStatuses.includes(pubStatus)
            })
        }

        // Competition filter
        if (competitionIds.length > 0) {
            const selectedIdSet = new Set(competitionIds)
            result = result.filter(m => m.tc_competition_id && selectedIdSet.has(m.tc_competition_id))
        }

        // Court filter
        if (courtIds.length > 0) {
            const selectedIdSet = new Set(courtIds)
            result = result.filter(m => m.tc_venue_id && selectedIdSet.has(m.tc_venue_id))
        }

        return result
    }, [allMatches, publishingStatuses, competitionIds, courtIds])

    // Filter helper functions for each pill
    // These are independent filters that can overlap

    // Live: start_time <= now AND (end_time IS NULL OR end_time > now) AND status NOT IN problem statuses
    const matchesLiveFilter = useCallback((match: LiveControlMatch): boolean => {
        if (!match.start_time) return false
        const now = new Date()
        const startTime = new Date(match.start_time)
        if (startTime > now) return false
        const isOngoing = !match.end_time || new Date(match.end_time) > now
        if (!isOngoing) return false
        // Exclude abandoned/cancelled
        const matchStatus = match.status?.toLowerCase()
        return !matchStatus || !PROBLEM_STATUSES.includes(matchStatus)
    }, [])

    // Upcoming 30min: start_time > now AND start_time <= now + 30min AND status NOT IN problem statuses
    const matchesUpcoming30Filter = useCallback((match: LiveControlMatch): boolean => {
        if (!match.start_time) return false
        const now = new Date()
        const startTime = new Date(match.start_time)
        const thirtyMinutesFromNow = new Date(now.getTime() + 30 * 60 * 1000)
        if (startTime <= now || startTime > thirtyMinutesFromNow) return false
        // Exclude abandoned/cancelled
        const matchStatus = match.status?.toLowerCase()
        return !matchStatus || !PROBLEM_STATUSES.includes(matchStatus)
    }, [])

    // Upcoming 48hr: start_time > now AND start_time <= now + 48hr AND status NOT IN problem statuses
    const matchesUpcoming48Filter = useCallback((match: LiveControlMatch): boolean => {
        if (!match.start_time) return false
        const now = new Date()
        const startTime = new Date(match.start_time)
        const fortyEightHoursFromNow = new Date(now.getTime() + 48 * 60 * 60 * 1000)
        if (startTime <= now || startTime > fortyEightHoursFromNow) return false
        // Exclude abandoned/cancelled
        const matchStatus = match.status?.toLowerCase()
        return !matchStatus || !PROBLEM_STATUSES.includes(matchStatus)
    }, [])

    // Recently Ended: end_time IS NOT NULL AND end_time > now - 3hr AND end_time <= now
    const matchesEndedFilter = useCallback((match: LiveControlMatch): boolean => {
        if (!match.end_time) return false
        const now = new Date()
        const endTime = new Date(match.end_time)
        const threeHoursAgo = new Date(now.getTime() - 3 * 60 * 60 * 1000)
        return endTime > threeHoursAgo && endTime <= now
    }, [])

    // Suspended: match status = 'suspended'
    const matchesSuspendedFilter = useCallback((match: LiveControlMatch): boolean => {
        return match.status?.toLowerCase() === 'suspended'
    }, [])

    // Problems: match status IN ('abandoned', 'cancelled') OR has publishing errors
    const matchesProblemsFilter = useCallback((match: LiveControlMatch): boolean => {
        const status = match.status?.toLowerCase()
        const hasMatchProblem = !!status && PROBLEM_STATUSES.includes(status)
        const hasAmagiError = match.publishing?.has_amagi_error || false
        const hasPublishError = match.publishing?.has_publish_error || false
        return hasMatchProblem || hasAmagiError || hasPublishError
    }, [])

    // Display-only helper for status dot color (preserves original display logic)
    // Shows 'upcoming-soon' color for matches the backend considers "upcoming" that start within 30 min
    const getStatusDotClass = useCallback((match: LiveControlMatch): string => {
        if (match.live_control_status === 'upcoming' && match.start_time) {
            const startTime = new Date(match.start_time)
            const now = new Date()
            const thirtyMinutesFromNow = new Date(now.getTime() + 30 * 60 * 1000)
            if (startTime <= thirtyMinutesFromNow && startTime > now) {
                return 'upcoming-soon'
            }
        }
        return match.live_control_status
    }, [])

    // Calculate status counts from fully-filtered matches (after all dropdown filters)
    // Each filter is applied independently - counts can overlap
    const statusCounts = useMemo(() => {
        const counts: Record<string, number> = {
            live: 0,
            'upcoming-soon': 0,
            upcoming: 0,
            ended: 0,
            suspended: 0,
            problem: 0,
        }
        for (const m of dropdownFilteredMatches) {
            if (matchesLiveFilter(m)) counts.live++
            if (matchesUpcoming30Filter(m)) counts['upcoming-soon']++
            if (matchesUpcoming48Filter(m)) counts.upcoming++
            if (matchesEndedFilter(m)) counts.ended++
            if (matchesSuspendedFilter(m)) counts.suspended++
            if (matchesProblemsFilter(m)) counts.problem++
        }
        return counts
    }, [dropdownFilteredMatches, matchesLiveFilter, matchesUpcoming30Filter, matchesUpcoming48Filter, matchesEndedFilter, matchesSuspendedFilter, matchesProblemsFilter])

    // Filter matches client-side based on selected status tab
    const filteredMatches = useMemo(() => {
        switch (status) {
            case 'all':
                return dropdownFilteredMatches
            case 'live':
                return dropdownFilteredMatches.filter(matchesLiveFilter)
            case 'upcoming-soon':
                return dropdownFilteredMatches.filter(matchesUpcoming30Filter)
            case 'upcoming':
                return dropdownFilteredMatches.filter(matchesUpcoming48Filter)
            case 'ended':
                return dropdownFilteredMatches.filter(matchesEndedFilter)
            case 'suspended':
                return dropdownFilteredMatches.filter(matchesSuspendedFilter)
            case 'problem':
                return dropdownFilteredMatches.filter(matchesProblemsFilter)
            default:
                return dropdownFilteredMatches
        }
    }, [dropdownFilteredMatches, status, matchesLiveFilter, matchesUpcoming30Filter, matchesUpcoming48Filter, matchesEndedFilter, matchesSuspendedFilter, matchesProblemsFilter])

    // Apply client-side pagination
    const paginatedMatches = useMemo(() => {
        const start = (page - 1) * limit
        return filteredMatches.slice(start, start + limit)
    }, [filteredMatches, page, limit])

    // Calculate pagination info from filtered results
    const pagination = useMemo(() => {
        const totalCount = filteredMatches.length
        const totalPages = Math.max(1, Math.ceil(totalCount / limit))
        return {
            page,
            limit,
            total_count: totalCount,
            total_pages: totalPages,
            has_next: page < totalPages,
            has_prev: page > 1,
        }
    }, [filteredMatches.length, page, limit])

    // Calculate "all" count - total unique matches (not sum of overlapping filters)
    const allCount = useMemo(() => {
        return dropdownFilteredMatches.length
    }, [dropdownFilteredMatches])

    const statusTabsWithCounts = useMemo(() => {
        return STATUS_TABS.map(tab => ({
            ...tab,
            count: tab.value === 'all' ? allCount : (statusCounts[tab.value] || 0),
        }))
    }, [statusCounts, allCount])

    const handleRefresh = useCallback(() => {
        mutate()
    }, [mutate])

    const getStatusBadgeVariant = (pubStatus: string): 'success' | 'warning' | 'error' | 'neutral' | 'secondary' => {
        switch (pubStatus) {
            case 'PUBLISHED':
                return 'success'
            case 'SCHEDULED':
                return 'warning'
            case 'DRAFT':
                return 'neutral'
            case 'CANCELLED':
            case 'WITHDRAWN':
                return 'error'
            default:
                return 'secondary'
        }
    }

    // Early return for error
    if (error) {
        return (
            <div className={pageStyles.pageContainer}>
                <div className={pageStyles.error}>
                    <h2>Error loading live control data</h2>
                    <p>Failed to fetch matches: {(error as Error).message}</p>
                    <Button variant="secondary" onClick={handleRefresh}>Retry</Button>
                </div>
            </div>
        )
    }

    return (
        <div className={pageStyles.pageContainer}>
            <div className={pageStyles.pageHeader}>
                <h1>Live & Upcoming Matches</h1>
                <p>Monitor and manage live events, upcoming matches, and recently completed content.</p>
            </div>

            {/* Search and Refresh Controls */}
            <div className={listTableStyles.searchSection}>
                <div className={styles.controlsRow}>
                    <div className={listTableStyles.filterGroup}>
                        <label htmlFor="search">Search by ID</label>
                        <input
                            type="text"
                            id="search"
                            value={search}
                            onChange={(e) => updateFilterAndResetPage({ search: e.target.value })}
                            placeholder="tc_match_id, ext_match_id, or tc_pub_content_id"
                            className={listTableStyles.searchInput}
                            style={{ minWidth: 280 }}
                        />
                    </div>

                    <div className={styles.refreshControls}>
                        <Button
                            variant="secondary"
                            size="small"
                            onClick={handleRefresh}
                            disabled={isLoading}
                            title="Refresh now"
                            className={isLoading ? styles.spinning : ''}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
                                <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
                                <path d="M21 3v5h-5" />
                            </svg>
                        </Button>

                        {lastUpdated && (
                            <span className={styles.lastUpdated}>
                                Updated: {lastUpdated.toLocaleTimeString()}
                                <br/>
                                (auto-refreshes every 1min)
                            </span>
                        )}
                    </div>
                </div>

                {/* Additional Filters - Row 1: Players, Categories, Publishing Status */}
                <div className={styles.filterRow}>
                    <div className={styles.filterField}>
                        <label>Players</label>
                        <CompetitorSelect
                            teamType="singles"
                            isMulti
                            value={selectedPlayers.length > 0 ? selectedPlayers : null}
                            onChange={(value) => {
                                const players = Array.isArray(value) ? value : (value ? [value] : [])
                                setSelectedPlayers(players)
                                const ids = players.map((p: any) => p.tc_id)
                                updateFilterAndResetPage({ playerIds: ids })
                            }}
                        />
                    </div>

                    <div className={styles.filterField}>
                        <label>Categories</label>
                        <Select
                            isMulti
                            isSearchable
                            isClearable
                            placeholder="Select categories..."
                            options={CATEGORY_OPTIONS}
                            value={categories.map(cat => ({
                                value: cat,
                                label: cat
                            }))}
                            onChange={(selectedOptions) => {
                                const selectedCategories = selectedOptions ? selectedOptions.map(option => option.value) : []
                                updateFilterAndResetPage({ categories: selectedCategories })
                            }}
                            className={selectStyles.reactSelect}
                            classNamePrefix="react-select"
                        />
                    </div>

                    <div className={styles.filterField}>
                        <label>Publishing Status</label>
                        <Select
                            isMulti
                            isSearchable
                            isClearable
                            placeholder="Select publishing statuses..."
                            options={PUBLISHING_STATUS_OPTIONS}
                            value={PUBLISHING_STATUS_OPTIONS.filter(opt =>
                                publishingStatuses.includes(opt.value)
                            )}
                            onChange={(selectedOptions) => {
                                const statuses = selectedOptions
                                    ? selectedOptions.map(opt => opt.value)
                                    : []
                                updateFilterAndResetPage({ publishingStatuses: statuses })
                            }}
                            className={selectStyles.reactSelect}
                            classNamePrefix="react-select"
                        />
                    </div>
                </div>

                {/* Additional Filters - Row 2: Competition, Court */}
                <div className={styles.filterRow}>
                    <div className={styles.filterField}>
                        <label>Competition</label>
                        <Select<FilterOption, true>
                            isMulti
                            isSearchable
                            isClearable
                            placeholder="Select competitions..."
                            options={competitionOptions}
                            value={competitionOptions.filter(opt => competitionIds.includes(opt.value))}
                            onChange={(selectedOptions) => {
                                const ids = selectedOptions ? selectedOptions.map(opt => opt.value) : []
                                updateFilterAndResetPage({ competitionIds: ids })
                            }}
                            className={selectStyles.reactSelect}
                            classNamePrefix="react-select"
                            noOptionsMessage={() => "No competitions in current view"}
                        />
                    </div>

                    <div className={styles.filterField}>
                        <label>Court</label>
                        <Select<FilterOption, true>
                            isMulti
                            isSearchable
                            isClearable
                            placeholder="Select courts..."
                            options={courtOptions}
                            value={courtOptions.filter(opt => courtIds.includes(opt.value))}
                            onChange={(selectedOptions) => {
                                const ids = selectedOptions ? selectedOptions.map(opt => opt.value) : []
                                updateFilterAndResetPage({ courtIds: ids })
                            }}
                            className={selectStyles.reactSelect}
                            classNamePrefix="react-select"
                            noOptionsMessage={() => "No courts in current view"}
                        />
                    </div>
                </div>
            </div>

            {/* Status Filter Tabs */}
            <ToggleGroup
                options={statusTabsWithCounts}
                value={status}
                onChange={(value) => {
                    updateFilterAndResetPage({ status: value })
                }}
            />

            {/* Table */}
            {isLoading && !data ? (
                <div className={pageStyles.loading}>
                    <h3>Loading matches...</h3>
                    <p>Please wait while we fetch the live control data.</p>
                </div>
            ) : paginatedMatches.length === 0 ? (
                <div className={listTableStyles.noResults}>
                    <h3>No matches found</h3>
                    <p>No matches match the current filters.</p>
                    <p>Time window: Next 48 hours (upcoming) / Last 3 hours (recent)</p>
                </div>
            ) : (
                <>
                    <div className={`${listTableStyles.tableHeader} ${styles.listHeader}`}>
                        <div></div>
                        <div>Match</div>
                        <div>Competition</div>
                        <div>Court</div>
                        <div>Start Time</div>
                        <div>Status</div>
                        <div>Publishing</div>
                        <div></div>
                    </div>

                    <div className={listTableStyles.tableBody}>
                        {paginatedMatches.map((match) => (
                            <div key={match.tc_match_id} className={`${listTableStyles.tableRow} ${styles.listItem}`}>
                                {/* Status Indicator */}
                                <div className={listTableStyles.cellText} style={{ display: 'flex', justifyContent: 'center' }}>
                                    <span className={`${styles.statusDot} ${styles[getStatusDotClass(match)]}`} />
                                </div>

                                {/* Match Title */}
                                <div className={listTableStyles.cellPrimary}>
                                    <p className={listTableStyles.primaryText}>
                                        {match.home_name} vs {match.away_name}
                                    </p>
                                    {match.round && (
                                        <p className={listTableStyles.secondaryText}>{match.round}</p>
                                    )}
                                </div>

                                {/* Competition */}
                                <div className={listTableStyles.cellText}>
                                    {match.competition_name || 'N/A'}
                                </div>

                                {/* Court/Venue */}
                                <div className={listTableStyles.cellText}>
                                    {match.venue_name ? (
                                        match.venue_city ? `${match.venue_name}, ${match.venue_city}` : match.venue_name
                                    ) : 'N/A'}
                                </div>

                                {/* Start Time */}
                                <div className={listTableStyles.cellPrimary}>
                                    <p className={listTableStyles.primaryText}>
                                        <FormattedDateTime dateString={match.start_time} />
                                    </p>
                                    {match.resume_time && (
                                        <p className={listTableStyles.secondaryText}>Resume: <FormattedDateTime dateString={match.resume_time} /></p>
                                    )}
                                </div>

                                {/* Match Status */}
                                <div className={listTableStyles.cellBadges}>
                                    {match.status && (
                                        <Badge variant="neutral">{formatMatchStatus(match.status)}</Badge>
                                    )}
                                </div>

                                {/* Publishing Status */}
                                <div className={listTableStyles.cellBadges}>
                                    {match.publishing?.status ? (
                                        <Badge
                                            variant={getStatusBadgeVariant(match.publishing.status)}
                                            clickable={!!match.publishing.tc_pub_content_id}
                                            onClick={() => match.publishing?.tc_pub_content_id && handleCopyPublishingId(match.tc_match_id, match.publishing.tc_pub_content_id)}
                                            icon={match.publishing.tc_pub_content_id ? (copiedMatchId === match.tc_match_id ? 'check' : 'clipboard') : undefined}
                                            ariaLabel={match.publishing.tc_pub_content_id ? `Copy Publishing ID ${match.publishing.tc_pub_content_id}` : undefined}
                                        >
                                            {copiedMatchId === match.tc_match_id ? 'Copied!' : match.publishing.status.charAt(0) + match.publishing.status.slice(1).toLowerCase()}
                                            {copiedMatchId !== match.tc_match_id && (match.publishing.has_amagi_error || match.publishing.has_publish_error) && ' ⚠️'}
                                        </Badge>
                                    ) : (
                                        <Badge variant="neutral">Unpublished</Badge>
                                    )}
                                    {match.publishing && ['PUBLISHED', 'SCHEDULED', 'DRAFT', 'UNPUBLISHED'].includes(match.publishing.status || 'UNPUBLISHED') && (
                                        <p className={`${listTableStyles.secondaryText} ${styles.publishingFeedText}`}>
                                            {match.publishing.feed_name || 'N/A'}
                                            {match.publishing.is_override && ' (override)'}
                                        </p>
                                    )}
                                    {match.publishing?.has_publish_error && (
                                        <Badge variant="error">
                                            Publish Error
                                        </Badge>
                                    )}
                                </div>

                                {/* Actions */}
                                <div className={listTableStyles.cellActions}>
                                    <Link
                                        to={`/matches/${match.tc_match_id}`}
                                        className={`${buttonStyles.button} ${buttonStyles.primary} ${buttonStyles.medium}`}
                                    >
                                        View
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>

                    {pagination && (
                        <Pagination
                            pagination={pagination}
                            onPageChange={handlePageChange}
                            itemName="matches"
                        />
                    )}
                </>
            )}
        </div>
    )
}

export default LiveControl
